import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACADEMY_LABELS, type ICourseTree, type IEnrollment } from '@stem/contracts';
import { apiClient } from '../services/api';

/**
 * A course, its modules and lessons, and the enrol button.
 *
 * The lesson list shows the premium badge but does not hide anything. A learner
 * on the free tier can see exactly which lesson is the virtual laboratory and
 * what it would take to open it, which is the only honest way to sell an
 * upgrade.
 */
export function CoursePage() {
  const { orgSlug = '', slug = '' } = useParams();
  const queryClient = useQueryClient();

  const course = useQuery({
    queryKey: ['course', slug],
    queryFn: () => apiClient.get<ICourseTree>(`/v1/catalog/courses/${slug}`),
  });

  const enrollments = useQuery({
    queryKey: ['enrollments'],
    queryFn: () => apiClient.get<Array<IEnrollment & { courseSlug: string }>>('/v1/learn/enrollments'),
  });

  const enrol = useMutation({
    mutationFn: (courseId: string) => apiClient.post<IEnrollment>('/v1/learn/enrollments', { courseId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['enrollments'] }),
  });

  if (course.isLoading) return <p className="text-sm text-ink-500">Loading…</p>;
  if (course.error) return <p className="text-sm text-red-700">{(course.error as Error).message}</p>;
  if (!course.data) return null;

  const tree = course.data;
  const enrollment = enrollments.data?.find(row => row.courseId === tree.id);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-brand-700">
          {ACADEMY_LABELS[tree.academy]}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-ink-900">{tree.title}</h1>
        <p className="mt-2 max-w-2xl text-sm text-ink-600">{tree.summary}</p>

        <div className="mt-6 space-y-4">
          {tree.modules.map((module, moduleIndex) => (
            <section key={module.id} className="card">
              <h2 className="text-base font-semibold text-ink-900">
                {moduleIndex + 1}. {module.title}
              </h2>
              {module.summary && <p className="mt-1 text-sm text-ink-600">{module.summary}</p>}

              <ol className="mt-3 divide-y divide-ink-100">
                {module.lessons.map(lesson => (
                  <li key={lesson.id}>
                    <Link
                      to={`/o/${orgSlug}/courses/${slug}/lessons/${lesson.id}`}
                      className="flex items-center gap-3 py-2.5 hover:text-brand-800"
                    >
                      <span aria-hidden="true" className="text-ink-400">
                        {lesson.publishedVersion === null ? '✎' : '▸'}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm">{lesson.title}</span>
                      {lesson.accessTier === 'premium' && (
                        <span className="badge-premium">premium</span>
                      )}
                      <span className="text-xs text-ink-500">{lesson.estimatedMinutes} min</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      </div>

      <aside className="space-y-4">
        <div className="card">
          {enrollment ? (
            <>
              <p className="text-sm font-semibold text-ink-900">You are enrolled</p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink-200">
                <div
                  className="h-full bg-emerald-600 transition-all"
                  style={{ width: `${enrollment.progressPercent}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-ink-500">{enrollment.progressPercent}% complete</p>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-700">
                Enrol to track your progress and earn a certificate.
              </p>
              <button
                type="button"
                className="btn-primary mt-3 w-full"
                disabled={enrol.isPending}
                onClick={() => enrol.mutate(tree.id)}
              >
                {enrol.isPending ? 'Enrolling…' : 'Enrol on this course'}
              </button>
            </>
          )}
        </div>

        {tree.outcomes.length > 0 && (
          <div className="card">
            <h3 className="text-sm font-semibold text-ink-900">Curriculum outcomes</h3>
            <p className="mb-2 text-xs text-ink-500">
              What this course is tagged against, and what a ministry coverage report counts.
            </p>
            <ul className="space-y-2">
              {tree.outcomes.map(outcome => (
                <li key={outcome.id} className="text-xs text-ink-600">
                  <span className="font-mono font-semibold text-ink-800">{outcome.code}</span>{' '}
                  {outcome.statement}
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}
